/**
 * The pages to start from — each one's name, what it is for, and its words,
 * written in the designer's language as a person would write them — and the
 * offer of them on a blank page.
 */
export const templates = {
  startTitle: 'Start from a template',
  lead: (survey: boolean, assistant: boolean) =>
    assistant
      ? `Pick one to make your own, describe what you need to the assistant, or start blank and add ${survey ? 'questions' : 'fields'} one by one.`
      : `Pick one to make your own, or start blank and add ${survey ? 'questions' : 'fields'} one by one.`,
  startBlank: 'Start blank',
  started: (title: string) => `Started from the template “${title}”.`,
  andMore: (n: number) => `and ${n} more`,
  startFrom: (title: string) => `Start from the template “${title}”`,
  template: 'template',
  undo: 'Undo',
  close: 'Close',
  // ---- refused
  notAPage: (issues: string) => `This is not a page the designer can open: ${issues}`,
  surveyNotScreen: 'A survey is made of pages of questions: this is a screen of sections',
  screenNotSurvey: 'A screen is made of sections: this is a survey’s pages of questions',
  // ---- starting from a schema (pageFromSchema in @fieldia/core)
  fromSchema: 'Start from a schema…',
  schema: {
    title: 'Start from a schema',
    lead: (survey: boolean) =>
      `Paste a JSON Schema, an OpenAPI document or a model’s fields as JSON, or choose its file: each property becomes a ${survey ? 'question' : 'field'} of its kind, with its label, its rules and its default.`,
    label: 'Schema, as JSON',
    file: 'Choose a file…',
    from: 'Start from',
    start: 'Start',
    cancel: 'Cancel',
    close: 'Close',
    hint: 'schema',
    fields: (n: number, survey: boolean) => (survey ? (n === 1 ? '1 question:' : `${n} questions:`) : n === 1 ? '1 field:' : `${n} fields:`),
    kept: 'Kept with a change:',
    leftOut: 'Left out:',
    started: (n: number, survey: boolean) => `Started from the schema: ${survey ? (n === 1 ? '1 question' : `${n} questions`) : n === 1 ? '1 field' : `${n} fields`}.`,
    empty: 'Paste a schema or choose a file first.',
    notJson: (problem: string) => `This is not JSON: ${problem}`,
    refused: {
      'not-json-object': 'A schema is a JSON object: a JSON Schema, an OpenAPI document or a model’s fields.',
      'not-a-schema': 'This is not a JSON Schema, an OpenAPI document or a model’s fields: it has no properties, no paths and no fields with a type.',
      'not-an-object': 'A page starts from an object, with properties: this schema is not one.',
      outside: 'This schema refers to one outside it: paste the schema itself.',
      'no-properties': 'This schema has no properties to ask for.',
      'no-such-schema': 'The document has no operation or schema of that name.',
      'nothing-in-document': 'This OpenAPI document has no operation with a body or parameters, and no schemas.',
    },
    /** What a schema said that was kept with a change, or left out (`SchemaNote`), said of its property. */
    note: (code: string, path: string, detail: string): string =>
      ({
        renamed: `${path} is named ${detail}: a field’s name has letters, digits and _ alone.`,
        flattened: `${path}’s properties are fields of their own, in a section of its name.`,
        json: `${path} is kept as JSON, edited as text: no field holds ${detail}.`,
        limit: `${path}: ${detail} is not checked by the page; your server checks it.`,
        format: `${path} is plain text: the format ${detail} is not one Fieldia knows.`,
        pattern: `${path}’s pattern ${detail} is not a regular expression, so it was left out.`,
        constant: `${path} is always ${detail}: shown, not asked.`,
        links: `${path} is links to ${detail} records: the fields of its lines were not given.`,
        languages: `${path} is typed in one language: name its languages on the page to keep it in several.`,
        reference: `${path} refers to ${detail}, which is not in the document.`,
        kind: `${path} holds ${detail}, which no field asks for.`,
      })[code] ?? `${path}: ${detail}`,
  },
  // ---- the templates' own words
  feedback: {
    title: 'Feedback',
    description: 'How it went, and what to do better, in two minutes.',
    page: 'Your feedback',
    overall: 'How was it overall?',
    liked: 'What did you like most?',
    better: 'What could we do better?',
    recommend: 'How likely are you to recommend us?',
    notLikely: 'Not likely',
    veryLikely: 'Very likely',
    email: 'Your email, for a reply',
    emailHelp: 'Only if you would like us to answer.',
  },
  event: {
    title: 'Event registration',
    description: 'Who is coming, and what they need on the day.',
    aboutYou: 'About you',
    fullName: 'Full name',
    email: 'Email',
    phone: 'Phone',
    organisation: 'Organisation',
    yourPlace: 'Your place',
    sessions: 'Which sessions will you join?',
    sessionChoices: ['Morning talks', 'Afternoon workshop', 'Evening dinner'],
    diet: 'Dietary needs',
    dietChoices: ['None', 'Vegetarian', 'Vegan', 'Gluten-free'],
    notes: 'Anything we should know?',
  },
  job: {
    title: 'Job application',
    description: 'Applications with a CV, ready to compare.',
    theRole: 'The role',
    role: 'Which role are you applying for?',
    roleChoices: ['Sales', 'Engineering', 'Operations'],
    cv: 'Your CV',
    experience: 'Years of experience',
    start: 'When could you start?',
    why: 'Why this role?',
    aboutYou: 'About you',
    fullName: 'Full name',
    email: 'Email',
    phone: 'Phone',
    profile: 'A link to your profile or work',
  },
  contact: {
    title: 'Contact',
    description: 'A person’s details, and notes about them.',
    contact: 'Contact',
    name: 'Name',
    company: 'Company',
    email: 'Email',
    phone: 'Phone',
    website: 'Website',
    notes: 'Notes',
  },
  order: {
    title: 'Order request',
    description: 'What a customer asks for, line by line.',
    order: 'Order',
    customer: 'Customer',
    status: 'Status',
    orderedOn: 'Ordered on',
    deliverBy: 'Deliver by',
    lines: 'Lines',
    items: 'Items',
    lineDescription: 'Description',
    quantity: 'Quantity',
    unitPrice: 'Unit price',
    total: 'Estimated total',
    notes: 'Notes',
  },
};

/** The app's assistant: its box on a blank page and in its dialog, and what it says back. */
export const assistant = {
  describe: 'Describe the form you need',
  describeExample: (survey: boolean): string =>
    survey ? 'A feedback form for a cooking class: a rating, what people liked most, and their email' : 'A supplier’s details: their name, a contact, the address and the bank account',
  build: 'Build it',
  building: 'Building your form…',
  built: 'The assistant built the form:',
  changed: 'The assistant changed the form:',
  askTitle: 'Ask the assistant',
  askItem: 'Ask the assistant…',
  hint: 'assistant',
  whatShouldChange: 'What should change?',
  changeExample: 'Add a question about the date, make the email required, put the rating first…',
  ask: 'Ask',
  changing: 'Changing your form…',
  cancel: 'Cancel',
  close: 'Close',
  askForWords: 'Say what the form is for, and what it should ask.',
  leftAsItWas: 'The assistant left the form as it was.',
  cancelled: 'Cancelled. Nothing was changed.',
  cannotBeUsed: (problem: string) => `The assistant’s form cannot be used: ${problem}`,
  /** It failed, saying why in its own words, or saying nothing. */
  couldNot: (why: string | null) => `The assistant could not do it${why ? `: ${why}` : '.'}`,
};
